import type { ComponentProps } from "react";
import {
  Box,
  Menu,
  MenuDivider,
  MenuIconButton,
  MenuItem,
  MenuList,
  OverflowMenuIcon,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { useHotkeys } from "react-hotkeys-hook";
import { pillMenuTriggerStyle } from "src/pages/projects/library/canvas/header-pill";

export type FileMenuItem = {
  label: string;
  icon: ComponentProps<typeof MenuItem>["icon"];
  onSelect: () => void;
  /** A react-hotkeys-hook combo, wired while the menu is closed and shown beside the item. */
  shortcut?: string;
  isDanger?: boolean;
  /** Starts a new group under a rule. */
  group?: boolean;
  /** Hightouch-only; the shared file menu puts it in its own group at the bottom. */
  isInternal?: boolean;
};

const isMac =
  typeof navigator === "object" && navigator.userAgent.includes("Mac");
const KEYS: Record<string, string> = isMac
  ? { mod: "⌘", alt: "⌥", shift: "⇧", backspace: "⌫" }
  : { mod: "Ctrl+", alt: "Alt+", shift: "Shift+", backspace: "Backspace" };
const formatShortcut = (shortcut: string) =>
  shortcut
    .split("+")
    .map((key) => KEYS[key] ?? key.toUpperCase())
    .join("");

/** A shortcut that works with its menu closed; it stands down while typing, like the editors' own. */
function ItemShortcut({
  item,
  isActive,
}: {
  item: FileMenuItem;
  isActive: boolean;
}) {
  useHotkeys(item.shortcut ?? "", item.onSelect, {
    enabled: isActive && Boolean(item.shortcut),
    preventDefault: true,
    // An open dialog has the keys, so a shortcut can't open a second one over it.
    ignoreEventWhen: (event) =>
      event.target instanceof Element &&
      event.target.closest('[aria-modal="true"]') !== null,
  });
  return null;
}

/** Wires each item's shortcut while its menu is closed. */
export function FileMenuShortcuts({
  items,
  isActive = true,
}: {
  items: FileMenuItem[];
  isActive?: boolean;
}) {
  return items.map((item) => (
    <ItemShortcut key={item.label} item={item} isActive={isActive} />
  ));
}

export function FileMenuItems({ items }: { items: FileMenuItem[] }) {
  return items.map((item) => (
    <Box key={item.label} display="contents">
      {item.group && <MenuDivider />}
      <MenuItem
        icon={item.icon}
        variant={item.isDanger ? "danger" : "normal"}
        onClick={item.onSelect}
      >
        <Row as="span" align="center" gap={4} justify="space-between">
          {item.label}
          {item.shortcut && (
            <Text as="span" size="sm" color="text.tertiary">
              {formatShortcut(item.shortcut)}
            </Text>
          )}
        </Row>
      </MenuItem>
    </Box>
  ));
}

/** The file's ···: every action it has, each with its shortcut. */
export function FileMoreMenu({
  items,
  areShortcutsActive = true,
}: {
  items: FileMenuItem[];
  /** Off while another file's editor is open over this one, so a shortcut acts on the file in front. */
  areShortcutsActive?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <>
      <FileMenuShortcuts items={items} isActive={areShortcutsActive} />
      {/* Fixed, like the history menu: in place, the unpositioned list scrolls the canvas's overflow-hidden row. */}
      <Menu placement="end" strategy="fixed" computePositionOnMount={false}>
        <Tooltip message="More actions">
          <MenuIconButton
            icon={OverflowMenuIcon}
            aria-label="More actions"
            variant="tertiary"
            shape="circle"
            {...pillMenuTriggerStyle}
          />
        </Tooltip>
        <MenuList>
          <FileMenuItems items={items} />
        </MenuList>
      </Menu>
    </>
  );
}
